import axios from "axios";
import {
  apiBaseUrl,
  APIM_BASE_URL,
  defaultRegion,
  oktaTokenEndpoint,
} from "@/constants";
import { resolveHttpTimeoutMs } from "@/utils/httpTimeout";
import { getAccessToken } from "@/utils/helper";
import Cookies from "js-cookie";

const apim = axios.create({
  timeout: 25000,
  headers: {
    "Content-type": "application/json",
  },
});

const httpTimeoutMs = resolveHttpTimeoutMs(
  process.env.NEXT_PUBLIC_HTTP_TIMEOUT_MS,
  process.env.HTTP_TIMEOUT_MS
);
const apiInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: httpTimeoutMs,
  headers: {
    "Content-type": "application/json",
    KohlerTarget: "AzureApp",
  },
});

export const getAPIAccessToken = async (apimHost = "") => {
  const channel = process.env.NEXT_PUBLIC_CHANNEL_NAME || "";
  try {
    const response = await axios.post(
      `${apimHost}${oktaTokenEndpoint}?scope=guest${
        channel ? `&channel=${channel}` : ""
      }`,
      null
    );
    const { access_token } = response.data;
    if (typeof window !== "undefined") {
      localStorage.setItem("okta-anonymous-access-token", access_token);
    }
    return { access_token };
  } catch (error) {
    console.error("Error fetching access token:", error);
    return null;
  }
};

apim.interceptors.request.use(
  async config => {
    config.baseURL = APIM_BASE_URL;
    config.headers = config.headers || {};
    const { token: accessToken = "" } = getAccessToken() || {};
    let access_token = accessToken;

    if (!accessToken) {
      const { access_token: newAccessToken } =
        (await getAPIAccessToken(APIM_BASE_URL)) || {};
      access_token = newAccessToken;
    }
    config.headers.Authorization = `Bearer ${access_token}`;

    const isLwApi = config.url && config.url.includes("/search/");

    if (isLwApi) {
      let region = defaultRegion;
      const selectedCountry = Cookies.get("selectedCountry");

      if (selectedCountry) {
        try {
          const parsed = JSON.parse(selectedCountry);
          region = parsed.region || defaultRegion;
        } catch (e) {
          console.error("Failed to parse selectedCountry cookie:", e);
        }
      }

      const buFilter = `buFilter_s:("${region}")`;
      const filterKey = "buFilter_s:"; // Used to detect existing filter

      // Case 1: If params is using URLSearchParams
      if (config.params instanceof URLSearchParams) {
        // Check if the parameter key 'fq' already contains our filter string
        const hasFilter = config.params
          .getAll("fq")
          .some(val => val.includes(filterKey));
        if (!hasFilter) {
          config.params.append("fq", buFilter);
        }
      }
      // Case 2: If params is a plain object or undefined
      else {
        config.params = config.params || {};
        const existingFq = config.params.fq;

        if (existingFq === undefined) {
          config.params.fq = buFilter;
        } else if (Array.isArray(existingFq)) {
          // Check if any element in the array already contains the filter string
          const hasFilter = existingFq.some(
            val => typeof val === "string" && val.includes(filterKey)
          );
          if (!hasFilter) {
            existingFq.push(buFilter);
          }
        } else if (typeof existingFq === "string") {
          // Check if the single string contains the filter string
          if (!existingFq.includes(filterKey)) {
            config.params.fq = [existingFq, buFilter];
          }
        }
      }
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

apim.interceptors.response.use(
  async res => {
    return res;
  },
  async error => {
    const originalConfig = error?.config || {};
    const isTokenEndpoint = originalConfig?.url?.includes(oktaTokenEndpoint);
    if (!isTokenEndpoint && error?.response?.status === 401) {
      originalConfig.headers = originalConfig.headers || {};
      return getAPIAccessToken(APIM_BASE_URL)
        .then(res => {
          const { access_token: newAccessToken } = res || {};
          originalConfig.headers.Authorization = `Bearer ${newAccessToken}`;
          return apim(originalConfig);
        })
        .catch(_error => {
          return Promise.reject(_error);
        });
    }
    return Promise.reject(error);
  }
);

export { apim, apiInstance };
